import { useEffect, useState } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import MDButton from '@components/MDButton';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { CrmScheduleService } from '../../kanban-board/services/crm-schedule.service';
import { LeadScheduleResponse } from '../../kanban-board/services/responses/lead-schedule.response';
import { ScheduleOutcome } from '../../kanban-board/constants/crm-schedule-outcome';

/// "Em aberto" é só editar; os outros encerram ou remarcam.
type Status = 'open' | ScheduleOutcome;

interface Props {
    action: LeadScheduleResponse | null;
    onClose: () => void;
    onSaved: () => void;
}

/// O datetime-local espera hora local sem fuso.
const toLocalInput = (iso: string) => {
    const date = new Date(iso);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

/// Edita uma ação aberta e dá o desfecho; reagendar usa a data do formulário como a nova data.
export const LeadActionModal = ({ action, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [description, setDescription] = useState('');
    const [when, setWhen] = useState('');
    const [status, setStatus] = useState<Status>('open');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!action) return;

        setDescription(action.description);
        setWhen(toLocalInput(action.scheduledAt));
        setStatus('open');
        setNote('');
    }, [action?.id]);

    const statusOptions = [
        { value: 'open', label: t('telaLead.acaoEmAberto') },
        { value: ScheduleOutcome.Completed, label: t('telaLead.acaoConcluida') },
        { value: ScheduleOutcome.Rescheduled, label: t('telaLead.acaoReagendada') },
        { value: ScheduleOutcome.NotReached, label: t('telaLead.acaoNaoAtendeu') },
    ];

    const field = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
        '& .MuiFormLabel-root': { color: cc.textSecondary },
    };

    const label = { fontSize: 12, fontWeight: 600, color: cc.textSecondary, display: 'block', mb: 0.6 };

    const save = async () => {
        if (!action || !description.trim() || !when) return;

        const service = new CrmScheduleService();
        const text = description.trim();
        const scheduledAt = new Date(when).toISOString();
        const textChanged = text !== action.description;
        const dateChanged = new Date(scheduledAt).getTime() !== new Date(action.scheduledAt).getTime();

        if (status === ScheduleOutcome.Rescheduled && !dateChanged) {
            showAlert(t('telaLead.reagendarPedeData'), 'warning', 4000);
            return;
        }

        setSaving(true);

        try {
            // Reagendar leva a data pelo desfecho (o histórico diz "reagendado"); aqui só o texto.
            if (status === ScheduleOutcome.Rescheduled) {
                if (textChanged) await service.update(action.id, { description: text, scheduledAt: action.scheduledAt });
            } else if (textChanged || dateChanged) {
                await service.update(action.id, { description: text, scheduledAt });
            }

            if (status !== 'open') {
                await service.close(action.id, {
                    outcome: status,
                    outcomeNote: note.trim() || undefined,
                    rescheduledTo: status === ScheduleOutcome.Rescheduled ? scheduledAt : undefined,
                });
            }

            showAlert(
                status === ScheduleOutcome.Completed ? t('telaLead.concluidaFoiProHistorico') : t('general.atualizacao'),
                'success',
                3000,
            );
            onSaved();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={action !== null} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pb: 0.5 }}>{t('telaLead.editarAcao')}</DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 2, pt: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('telaLead.oQueFazer')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    inputProps={{ maxLength: 200 }}
                    sx={field}
                />

                <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label={status === ScheduleOutcome.Rescheduled ? t('telaLead.novaData') : t('agenda.quando')}
                    value={when}
                    onChange={(e) => setWhen(e.target.value)}
                    sx={field}
                />

                <div>
                    <Typography sx={label}>{t('telaLead.desfechoDaAcao')}</Typography>
                    <StandaloneAutoCompleteSelect
                        options={statusOptions}
                        value={status}
                        onChange={(value) => setStatus((value as Status) ?? 'open')}
                    />
                </div>

                {status !== 'open' && (
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        label={t('telaLead.observacao')}
                        placeholder={t('telaLead.observacaoPlaceholder')}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        sx={field}
                    />
                )}
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {t('general.cancelar')}
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="success"
                    onClick={save}
                    disabled={saving || !description.trim() || !when}
                >
                    {t('general.salvar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
